import {
  CalendarDays,
  LayoutDashboard,
  Music4,
  Settings,
  UsersRound,
  Wrench,
} from 'lucide-react'

export interface NavItem {
  to: string
  label: string
  icon: typeof LayoutDashboard
  /** `end` so `/` does not stay highlighted on every child route. */
  end?: boolean
  description: string
  /** Minimum role that should see this destination at all. */
  minRole?: 'member' | 'viewer' | 'scheduler' | 'admin'
  /**
   * Never offered on a phone, whatever the role.
   *
   * Ian, 2026-10-20: the app on a phone is for the person who is serving on
   * Sunday — what am I on, what are we singing, what is the running order.
   * Building the rota, editing team structure and the API keys are desk
   * work, and a tab bar that carries them makes the three things a volunteer
   * actually opens harder to reach. They are not hidden from an admin, they
   * are on the laptop where they are usable.
   */
  desktopOnly?: boolean
}

export const NAV_ITEMS: NavItem[] = [
  { to: '/', label: 'Home', icon: LayoutDashboard, end: true, description: 'The dates you are serving' },
  { to: '/plans', label: 'Service Plans', icon: CalendarDays, description: 'What is coming up, and the order of service' },
  { to: '/songs', label: 'Song Library', icon: Music4, description: 'Songs, keys and arrangements' },
  {
    to: '/schedule',
    label: 'Volunteers',
    icon: UsersRound,
    description: 'Who is serving, their status and blockouts',
    minRole: 'scheduler',
    desktopOnly: true,
  },
  {
    to: '/teams',
    label: 'Teams',
    icon: Wrench,
    description: 'Ministries, teams and positions',
    minRole: 'scheduler',
    desktopOnly: true,
  },
  {
    to: '/admin',
    label: 'Admin',
    icon: Settings,
    description: 'Accounts, keys and settings',
    minRole: 'admin',
    desktopOnly: true,
  },
]

const RANK: Record<string, number> = { member: 1, viewer: 2, scheduler: 3, admin: 4 }

/** True when this role is at or above `min`. The roles are a ladder. */
export function atLeast(role: string | undefined, min: NavItem['minRole']): boolean {
  return (RANK[role ?? 'member'] ?? 1) >= (RANK[min ?? 'member'] ?? 1)
}

/**
 * The destinations a given role should be offered.
 *
 * `phone` drops everything marked `desktopOnly`. It is a separate argument
 * rather than a media query inside here because this runs in the tab bar, the
 * sidebar and the route guards, and only one of those is a phone.
 */
export function navItemsFor(role: string, options: { phone?: boolean } = {}): NavItem[] {
  return NAV_ITEMS.filter((item) => {
    if (options.phone && item.desktopOnly) return false
    return atLeast(role, item.minRole)
  })
}
